import { useEffect, useRef } from "react";
import type { RowData } from "@tanstack/react-table";
import type { DataGridInstance } from "../types";
import type { GridController } from "./gridController";
import { isEditableTarget } from "./editableTarget";
import {
    findFirstFocusableRow,
    getListNavigableColumnIds,
    getRowModeColumnId,
} from "./navigation";

/**
 * Focuses a row, or a cell in cell navigation (the row-mode column when
 * `columnId` is omitted). List editing focuses the cell when `columnId` names
 * a visible column it steps through, and the row otherwise.
 */
export const focusTableRow = <TData extends RowData>({
    instance,
    controller,
    rowId,
    columnId,
}: {
    instance: DataGridInstance<TData>;
    controller: GridController;
    rowId: string;
    columnId?: string;
}) => {
    const targetColumnId = columnId ?? getRowModeColumnId(instance.table);
    if (targetColumnId === null) {
        return;
    }
    const isListCell =
        instance.hasListEditing &&
        columnId !== undefined &&
        getListNavigableColumnIds(instance.table).includes(columnId);
    instance.table.setFocusedCell(rowId, targetColumnId);
    if (instance.hasListEditing) {
        instance.atoms.listCellFocus.set(isListCell);
    }
    controller.focusTarget({
        rowId,
        columnId:
            instance.hasCellNavigation || isListCell ? targetColumnId : null,
    });
};

/**
 * Focuses the first row (or cell) once rows are ready, so table keys work
 * without a click. `true` focuses once per mount; a string focuses again
 * whenever it changes (pass the open record's id). Leaves focus alone while
 * the user types in a field or already has focus in the table body. The
 * focus visuals stay hidden until the user acts in the grid; screen readers
 * still land in it. Retries on the new first row when the first one was
 * replaced before it mounted (a follow-up query changed its id).
 */
export const useAutoFocus = <TData extends RowData>({
    instance,
    controller,
    autoFocus,
}: {
    instance: DataGridInstance<TData>;
    controller: GridController;
    autoFocus: boolean | string | undefined;
}) => {
    const focusedKeyRef = useRef<string | true | null>(null);
    const requestedRowIdRef = useRef<string | null>(null);
    const firstRowId =
        instance.status.state === "ready"
            ? findFirstFocusableRow(
                  instance.table.getRowModel().rows,
                  instance.rowKinds.isSeparator,
              )?.id
            : undefined;

    useEffect(() => {
        if (
            autoFocus === undefined ||
            autoFocus === false ||
            firstRowId === undefined
        ) {
            return;
        }
        const isRetry =
            focusedKeyRef.current === autoFocus &&
            requestedRowIdRef.current !== firstRowId &&
            controller.hasPendingFocus();
        if (focusedKeyRef.current === autoFocus && !isRetry) {
            return;
        }
        focusedKeyRef.current = autoFocus;
        const active = document.activeElement;
        if (
            isEditableTarget(active) ||
            (active !== null && controller.body?.contains(active))
        ) {
            return;
        }
        requestedRowIdRef.current = firstRowId;
        focusTableRow({ instance, controller, rowId: firstRowId });
        controller.setFocusQuiet("all");
    }, [autoFocus, controller, firstRowId, instance]);
};
